import * as yup from 'yup';
import { EmployeeOffboardingAppointmentHandling } from '@prisma/client';
import { OFFBOARDING_MAX_DAYS_AHEAD } from '../../constants/offboardings';
import {
  Offboarding,
  OffboardingWizardMode,
  OffboardingWizardValues,
  StepConfig,
  StepId,
} from '../../types/offboardings';

export const offboardingWizardSchema = yup.object({
  mode: yup
    .mixed<OffboardingWizardMode>()
    .oneOf(['IMMEDIATE', 'SCHEDULED'])
    .required(),
  scheduledFor: yup
    .string()
    .default('')
    .when('mode', {
      is: 'SCHEDULED',
      then: schema =>
        schema
          .required('Pick a date and time')
          .test(
            'in-future',
            'The scheduled time must be in the future',
            value => !!value && new Date(value).getTime() > Date.now(),
          )
          .test(
            'within-horizon',
            `Offboardings can be scheduled at most ${OFFBOARDING_MAX_DAYS_AHEAD} days in the future`,
            value => {
              if (!value) return true;
              const maxMs =
                Date.now() + OFFBOARDING_MAX_DAYS_AHEAD * 24 * 60 * 60 * 1000;
              return new Date(value).getTime() <= maxMs;
            },
          ),
    }),
  appointmentHandling: yup
    .mixed<EmployeeOffboardingAppointmentHandling>()
    .oneOf(Object.values(EmployeeOffboardingAppointmentHandling))
    .required(),
  retainCustomerAccess: yup
    .mixed<'true' | 'false'>()
    .oneOf(['true', 'false'])
    .required(),
});

export function buildOffboardingPayload(values: OffboardingWizardValues) {
  return {
    scheduledFor:
      values.mode === 'SCHEDULED' && values.scheduledFor
        ? new Date(values.scheduledFor).toISOString()
        : null,
    appointmentHandling: values.appointmentHandling,
    retainCustomerAccess: values.retainCustomerAccess === 'true',
  };
}

export function offboardingToWizardValues(
  offboarding: Offboarding,
): OffboardingWizardValues {
  const scheduledFor = offboarding.scheduledFor
    ? new Date(offboarding.scheduledFor)
    : null;
  const isFuture = !!scheduledFor && scheduledFor.getTime() > Date.now();
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    mode: isFuture ? 'SCHEDULED' : 'IMMEDIATE',
    scheduledFor:
      isFuture && scheduledFor
        ? `${scheduledFor.getFullYear()}-${pad(
            scheduledFor.getMonth() + 1,
          )}-${pad(scheduledFor.getDate())}T${pad(
            scheduledFor.getHours(),
          )}:${pad(scheduledFor.getMinutes())}`
        : '',
    appointmentHandling: offboarding.appointmentHandling,
    retainCustomerAccess: offboarding.retainCustomerAccess ? 'true' : 'false',
  };
}

export function resolveStepHelperText(
  step: StepConfig,
  ctx: {
    values: OffboardingWizardValues;
    isEdit: boolean;
    employeeName: string;
  },
): string | null {
  if (!step.helperText) return null;
  if (typeof step.helperText === 'string') return step.helperText;
  return step.helperText(ctx);
}

export function reviewStepForEdit(step: StepConfig): StepConfig {
  return {
    ...step,
    eyebrow: 'Overview',
    headline: 'Edit offboarding',
    lede: 'Update any field below and save when you\u2019re ready. Closing will leave the offboarding as it is.',
  };
}

export function isOffboardingStepComplete(
  step: StepId,
  values: OffboardingWizardValues,
  errors: Record<string, unknown>,
): boolean {
  switch (step) {
    case 'when':
      return (
        values.mode === 'IMMEDIATE' ||
        (!!values.scheduledFor && !errors.scheduledFor)
      );
    case 'appointments':
      return !!values.appointmentHandling;
    case 'access':
      return !!values.retainCustomerAccess;
    case 'review':
    default:
      return true;
  }
}
